import React from "react";

/**
 * Lightweight SVG visualizer for geometry problems.
 * Accepts a `visual` object produced by mathGen.js:
 *  - { type: "cuadrado", lado }
 *  - { type: "rectangulo", base, altura }
 *  - { type: "triangulo", a, b, c }
 *  - { type: "triangulo-area", base, altura }
 *  - { type: "circulo", r }
 *  - { type: "pitagoras", a, b, c }
 */
export function GeometryVisual({ visual, color = "#10B981", unit = "cm" }) {
  if (!visual) return null;

  const stroke = color;
  const fill = `${color}22`;

  if (visual.type === "cuadrado") {
    const { lado } = visual;
    return (
      <svg viewBox="0 0 200 200" className="w-44 h-44 sm:w-56 sm:h-56" data-testid="geom-svg-cuadrado">
        <rect x="40" y="40" width="120" height="120" stroke={stroke} strokeWidth="4" fill={fill} />
        <text x="100" y="32" fontSize="16" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          {lado} {unit}
        </text>
        <text x="100" y="178" fontSize="16" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          {lado} {unit}
        </text>
      </svg>
    );
  }

  if (visual.type === "rectangulo") {
    const { base, altura } = visual;
    return (
      <svg viewBox="0 0 240 180" className="w-52 h-40 sm:w-64 sm:h-48" data-testid="geom-svg-rectangulo">
        <rect x="30" y="40" width="180" height="100" stroke={stroke} strokeWidth="4" fill={fill} />
        <text x="120" y="32" fontSize="16" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          {base} {unit}
        </text>
        <text x="18" y="95" fontSize="16" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          {altura}
        </text>
      </svg>
    );
  }

  if (visual.type === "triangulo") {
    const { a, b, c } = visual;
    return (
      <svg viewBox="0 0 220 180" className="w-52 h-40 sm:w-64 sm:h-48" data-testid="geom-svg-triangulo">
        <polygon points="20,160 200,160 110,30" stroke={stroke} strokeWidth="4" fill={fill} />
        <text x="60" y="178" fontSize="15" fontWeight="700" fill="#1C1C1E">{a} {unit}</text>
        <text x="160" y="100" fontSize="15" fontWeight="700" fill="#1C1C1E">{b}</text>
        <text x="40" y="100" fontSize="15" fontWeight="700" fill="#1C1C1E">{c}</text>
      </svg>
    );
  }

  if (visual.type === "triangulo-area") {
    const { base, altura } = visual;
    return (
      <svg viewBox="0 0 220 180" className="w-52 h-40 sm:w-64 sm:h-48" data-testid="geom-svg-triangulo-area">
        <polygon points="30,160 200,160 110,30" stroke={stroke} strokeWidth="4" fill={fill} />
        {/* Altura line */}
        <line x1="110" y1="30" x2="110" y2="160" stroke={stroke} strokeWidth="2" strokeDasharray="4 4" />
        <text x="115" y="155" fontSize="13" fill={stroke}>┘</text>
        <text x="115" y="100" fontSize="15" fontWeight="700" fill="#1C1C1E">h = {altura}</text>
        <text x="115" y="178" fontSize="15" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          b = {base} {unit}
        </text>
      </svg>
    );
  }

  if (visual.type === "circulo") {
    const { r } = visual;
    return (
      <svg viewBox="0 0 200 200" className="w-44 h-44 sm:w-56 sm:h-56" data-testid="geom-svg-circulo">
        <circle cx="100" cy="100" r="70" stroke={stroke} strokeWidth="4" fill={fill} />
        <line x1="100" y1="100" x2="170" y2="100" stroke={stroke} strokeWidth="3" />
        <circle cx="100" cy="100" r="4" fill={stroke} />
        <text x="135" y="92" fontSize="15" fontWeight="700" fill="#1C1C1E">r = {r} {unit}</text>
      </svg>
    );
  }

  if (visual.type === "pitagoras") {
    const { a, b, c, unknown } = visual;
    const showA = unknown === "a" ? "?" : a;
    const showB = unknown === "b" ? "?" : b;
    const showC = unknown === "c" ? "?" : c;
    return (
      <svg viewBox="0 0 220 180" className="w-52 h-40 sm:w-64 sm:h-48" data-testid="geom-svg-pitagoras">
        <polygon points="30,160 200,160 30,40" stroke={stroke} strokeWidth="4" fill={fill} />
        {/* Right-angle marker */}
        <rect x="30" y="146" width="14" height="14" stroke={stroke} strokeWidth="2" fill="white" />
        <text x="110" y="178" fontSize="15" fontWeight="700" textAnchor="middle" fill="#1C1C1E">
          a = {showA} {unit}
        </text>
        <text x="0" y="100" fontSize="15" fontWeight="700" fill="#1C1C1E">b = {showB}</text>
        <text x="120" y="95" fontSize="15" fontWeight="700" fill="#1C1C1E">c = {showC}</text>
      </svg>
    );
  }

  return null;
}
